/**
 * Hand-drawn map annotations — the one player-writable map primitive.
 *
 * Visual only: an annotation has no rules effect (no LoS, movement, or cover), and the
 * engine never reads one, exactly like a {@link Room}.
 */

/** Id type for {@link Annotation} — a string, matching {@link RoomId}'s convention. */
export type AnnotationId = string;

/**
 * The small fixed **holo palette** an annotation's stroke color is drawn from. Cyan is
 * already spoken for by walls and pink by hostile tokens; amber reads as hazard/warning
 * — so an annotation DEFAULTS to {@link DEFAULT_ANNOTATION_COLOR} (`"green"`), the one
 * member of the set with no other established meaning on the combat surface, so a fresh
 * stroke never reads as a wall/hostile/warning by accident. All four remain pickable (a
 * GM/player may deliberately draw in cyan to mark "wall-like", etc).
 */
export const ANNOTATION_COLORS = ["cyan", "amber", "pink", "green"] as const;
export type AnnotationColor = (typeof ANNOTATION_COLORS)[number];
/** The default color a fresh annotation is drawn in — see {@link ANNOTATION_COLORS}. */
export const DEFAULT_ANNOTATION_COLOR: AnnotationColor = "green";

/**
 * A click-vertex polyline or a freehand stroke, table-shared. Two brushes share one
 * shape via the `kind` discriminator: `"polyline"` stores its clicked vertices verbatim
 * (deliberate placement, no simplification); `"freehand"` stores an already-simplified
 * stroke (distance-sampled during the drag, then Ramer-Douglas-Peucker on release — the
 * authoring layer's job, never the reducer's). `points` are **world-px, scale-1**
 * `{x, y}` — continuous, never cell-quantized — and are NOT spatially clamped to the map
 * bounds (purely visual; an off-board point is legal, exactly like a room's rects are
 * never bounds-checked).
 *
 * `authorSlotId` is the **forge-proof ownership key** — the exact `controllerSlotId`
 * mechanism combatants use: the client sets it, but the server-stamped `command.actorId`
 * is what the ownership gate actually checks against, so a player cannot forge another
 * participant's authorship. `null` means GM-authored (no player owner), the same
 * convention `controllerSlotId: null` carries for a GM-owned combatant.
 *
 * Stored id-addressed in {@link EncounterState.annotations}, read through
 * {@link encounterAnnotations} so a blob predating annotations defaults cleanly. Plain
 * JSON, no class — the same wire/persist invariant as {@link Room}.
 */
export type Annotation = {
  id: AnnotationId;
  kind: "polyline" | "freehand";
  /** World-px, scale-1 vertices — see the type doc for why no cell-quantization/clamp. */
  points: { x: number; y: number }[];
  color: AnnotationColor;
  /** The authoring slot's id, or `null` for a GM-authored (unowned) annotation. */
  authorSlotId: string | null;
};

/**
 * The hard cap on an annotation's `points.length`, enforced by the reducer on
 * `add_annotation`/`set_annotation`: every `EncounterState` change broadcasts the
 * **whole state** (LWW, no delta protocol), so an unbounded freehand stroke would
 * inflate every subsequent broadcast, not just its own. 256 bounds the worst case for a
 * single stroke's wire payload while sitting far above any real screen-bounded scribble
 * after simplification (typically well under 50 points). A polyline's deliberately-
 * clicked vertices are expected to stay a handful — the cap is a backstop, not a design
 * target either brush should routinely approach.
 */
export const MAX_ANNOTATION_POINTS = 256;
